import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { AgentMessage, Message, MessageType } from "./AgentMessage";
import { AgentModeDropdown, ChatMode } from "./AgentModeDropdown";
import { ModelDropdown, ModelType } from "./ModelDropdown";
import { AttachmentDropdown } from "../editor/AttachmentDropdown";
import {
  Send,
  StopCircle,
  Loader2,
  X,
  FileIcon,
  FileText,
  Upload,
  Bot,
  Eye,
  ArrowDown,
  Mic,
  RefreshCw,
  MoreHorizontal,
  MessageCircle,
  Plus,
} from "lucide-react";
import { MentionDropdown, type MentionSelection } from "./mention-dropdown";
import { MentionBadge } from "./mention-badge";
import { editorAPI } from "@/lib/editor/api";
import { cn } from "@/lib/utils";
import { useResponsive } from "@/hooks/useResponsive";
import { useSpeechRecognition } from "@/hooks/use-speech-recognition";
import { CreditsWarning, CreditAlertLevel } from "./CreditsDisplay";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "@/hooks/usePermissions";
import type { ThemeContent } from "@/types/theme.types";
import { useThemeEditor } from "@/hooks/use-theme-editor";
import toast from "react-hot-toast";

interface FileWithState {
  file: File;
  uploadState?: "pending" | "uploading" | "completed" | "failed";
  uploadProgress?: number;
  error?: string;
}

interface AgentChatProps {
  messages: Message[];
  mode: ChatMode;
  model: ModelType;
  onModeChange: (mode: ChatMode) => void;
  onModelChange: (model: ModelType) => void;
  onSendPrompt: (prompt: string) => void;
  onStopChat?: () => void;
  onClearChat?: () => void;
  isStreaming?: boolean;
  disabled?: boolean;
  onSelectAttach?: () => void;
  onSelectConnections?: () => void;
  onSelectProjectSettings?: () => void;
  onSelectImageGenerator?: () => void;
  onSelectMediaLibrary?: () => void;
  onSelectThemes?: () => void;
  attachedFile?: File | null;
  attachedFiles?: FileWithState[];
  onRemoveAttachment?: (fileName?: string) => void;
  onFilesDropped?: (files: File[]) => void;
  onOpenFile?: (filePath: string) => void;
  onAction?: (actionId: string, messageId: string) => void;
  onOpenVisualEditor?: () => void;
  onOpenHistory?: () => void;
  devContainerId?: string;
  projectId?: string;
  sessionHeaderActions?: React.ReactNode;
  mobileActiveTab?: string;
  onMobileActiveTabChange?: (tab: string) => void;
  onOpenSettings?: () => void;
  creditBalance?: number | null;
  allocatedCredits?: number | null;
  isLoadingCredits?: boolean;
  shownCreditAlerts?: Set<CreditAlertLevel>;
  onCreditAlertShown?: (level: CreditAlertLevel) => void;
  byocConnected?: boolean;
  isEmailVerificationBlocked?: boolean;
  onCheckEmailVerification?: () => Promise<void>;
  isCheckingEmailVerification?: boolean;
  onResendVerificationEmail?: () => Promise<void>;
  isResendingVerificationEmail?: boolean;
  isContainerReady?: boolean;
  // Connection lost banner
  connectionLost?: boolean;
  onConnectionLostDismiss?: () => void;
  // Theme preview props (required for ThemePreviewCard in ClaudeResponseCard)
  workspaceId: string;
  previewIframe: HTMLIFrameElement | null;
  previewUrl?: string;
  onEditTheme?: (theme: ThemeContent) => void;
  onThemeSaved?: (themeId: string) => void;
  onConnectionCreated?: (
    connectionId: string,
    connectorType: string,
    connectionName: string,
  ) => void;
}

export function AgentChat({
  messages,
  mode,
  model,
  onModeChange,
  onModelChange,
  onSendPrompt,
  onStopChat,
  onClearChat,
  isStreaming = false,
  disabled = false,
  onSelectAttach,
  onSelectConnections,
  onSelectProjectSettings,
  onSelectImageGenerator,
  onSelectMediaLibrary,
  onSelectThemes,
  attachedFile,
  attachedFiles = [],
  onRemoveAttachment,
  onFilesDropped,
  onOpenFile,
  onAction,
  onOpenVisualEditor,
  onOpenHistory,
  devContainerId,
  projectId,
  sessionHeaderActions,
  mobileActiveTab,
  onMobileActiveTabChange,
  onOpenSettings,
  creditBalance = null,
  allocatedCredits = null,
  isLoadingCredits = false,
  shownCreditAlerts,
  onCreditAlertShown,
  byocConnected = false,
  isEmailVerificationBlocked = false,
  onCheckEmailVerification,
  isCheckingEmailVerification = false,
  onResendVerificationEmail,
  isResendingVerificationEmail = false,
  isContainerReady = true,
  connectionLost = false,
  onConnectionLostDismiss,
  workspaceId,
  previewIframe,
  previewUrl,
  onEditTheme,
  onThemeSaved,
  onConnectionCreated,
}: AgentChatProps) {
  const navigate = useNavigate();
  const { canUseAgent, isViewer } = usePermissions();

  // Check if credits are insufficient (balance <= 0)
  // BYOC users bypass credit checks - they pay Anthropic directly
  const numBalance =
    creditBalance !== null
      ? typeof creditBalance === "string"
        ? parseFloat(creditBalance as string)
        : creditBalance
      : null;
  const isInsufficientCredits =
    !byocConnected && numBalance !== null && numBalance <= 0;

  // Check if chat should be blocked (insufficient credits OR email not verified OR viewer role)
  const isChatBlocked =
    isInsufficientCredits || isEmailVerificationBlocked || !canUseAgent;

  // Use attachedFiles if provided, otherwise fall back to single attachedFile for backward compatibility
  const files: FileWithState[] =
    attachedFiles.length > 0
      ? attachedFiles
      : attachedFile
        ? [{ file: attachedFile, uploadState: "completed" }]
        : [];
  const [prompt, setPrompt] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  // @ mention state
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionStartIndex, setMentionStartIndex] = useState<number | null>(
    null,
  );
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  // Cross-project referencing state
  const [referenceableProjects, setReferenceableProjects] = useState<
    {
      id: string;
      name: string;
      description: string | null;
      mode: string;
      devContainerStatus: string | null;
    }[]
  >([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [mentionFiles, setMentionFiles] = useState<string[]>([]);
  const [mentionChips, setMentionChips] = useState<
    {
      type: "project" | "file" | "theme" | "skill";
      name: string;
      id?: string;
    }[]
  >([]);
  const [filePreviews, setFilePreviews] = useState<Map<string, string>>(
    new Map(),
  );
  const [isDragging, setIsDragging] = useState(false);
  const mentionConfirmRef = useRef<(() => void) | null>(null);
  const mentionSwitchTabRef = useRef<
    ((direction: "left" | "right") => void) | null
  >(null);
  const mentionItemCountRef = useRef<number>(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const userScrolledDuringStream = useRef(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState("Thinking...");
  // Use external state if provided, otherwise use internal state
  const [internalMobileActiveTab, setInternalMobileActiveTab] =
    useState("chat");
  const currentActiveTab =
    mobileActiveTab !== undefined ? mobileActiveTab : internalMobileActiveTab;
  const { isMobile } = useResponsive();

  // Voice recognition hook
  const {
    isRecording,
    isProcessing: isProcessingVoice,
    startRecording,
    stopRecording,
  } = useSpeechRecognition({
    onResult: (transcript) => {
      setPrompt(transcript);
    },
  });

  // Generic rotating status messages (30+)
  const streamingMessages = [
    "Thinking...",
    "Working on it...",
    "Processing...",
    "Just a moment...",
    "Almost there...",
    "Getting there...",
    "Making progress...",
    "One moment...",
    "Please wait...",
    "Working...",
    "On it...",
    "In progress...",
    "Hang tight...",
    "Computing...",
    "Calculating...",
    "Figuring it out...",
    "Bear with me...",
    "Nearly done...",
    "Almost ready...",
    "Wrapping up...",
    "Finalizing...",
    "Just about there...",
    "Give me a sec...",
    "Hold on...",
    "Coming up...",
    "A bit more...",
    "Getting ready...",
    "Preparing...",
    "Stay tuned...",
    "Loading...",
    "Busy...",
    "Running...",
  ];

  // Handle voice recording toggle
  const handleVoiceToggle = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      setPrompt(""); // Clear previous chat input
      startRecording();
    }
  }, [isRecording, startRecording, stopRecording]);

  // Randomly rotate streaming message every 4 seconds
  useEffect(() => {
    if (!isStreaming) {
      setStreamingMessage("Thinking...");
      return;
    }

    const interval = setInterval(() => {
      const randomIndex = Math.floor(Math.random() * streamingMessages.length);
      setStreamingMessage(streamingMessages[randomIndex]);
    }, 4000);

    return () => clearInterval(interval);
  }, [isStreaming]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dragCounter = useRef(0);
  const lastEscTimeRef = useRef<number>(0);
  const ESC_TIMEOUT = 500; // 500ms timeout for double ESC

  // Generate preview URLs for image files
  useEffect(() => {
    // Cleanup old URLs
    filePreviews.forEach((url) => URL.revokeObjectURL(url));

    const newPreviews = new Map<string, string>();

    files.forEach((fileWithState) => {
      if (fileWithState.file.type?.startsWith("image/")) {
        const url = URL.createObjectURL(fileWithState.file);
        newPreviews.set(fileWithState.file.name, url);
      }
    });

    setFilePreviews(newPreviews);

    // Cleanup URLs when component unmounts
    return () => {
      newPreviews.forEach((url) => URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files.length]);

  // Auto-scroll to bottom when new messages arrive
  // Skip auto-scroll only if: streaming is active AND user has scrolled up
  useEffect(() => {
    if (!messagesContainerRef.current) return;

    const container = messagesContainerRef.current;

    const scrollToBottom = (smooth = true) => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTo({
          top: messagesContainerRef.current.scrollHeight,
          behavior: smooth ? "smooth" : "instant",
        });
      }
    };

    // Check if we should skip scroll (user scrolled up during streaming)
    const shouldSkipScroll = () =>
      isStreaming && userScrolledDuringStream.current;

    // Initial scroll when messages change (unless user scrolled up during streaming)
    if (!shouldSkipScroll()) {
      scrollToBottom(false);
    }

    // Use MutationObserver to scroll after DOM changes settle
    let scrollTimeout: NodeJS.Timeout;
    const observer = new MutationObserver(() => {
      // Check INSIDE the callback - user may have scrolled up since observer was set up
      if (shouldSkipScroll()) return;

      // Debounce: scroll after DOM stops changing for 50ms
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        if (!shouldSkipScroll()) {
          scrollToBottom(true);
        }
      }, 50);
    });

    observer.observe(container, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    // Also scroll after a max delay as fallback
    const maxTimeout = setTimeout(() => {
      if (!shouldSkipScroll()) {
        scrollToBottom(true);
      }
      observer.disconnect();
    }, 300);

    return () => {
      clearTimeout(scrollTimeout);
      clearTimeout(maxTimeout);
      observer.disconnect();
    };
  }, [messages, isStreaming]);

  // Auto-scroll when streaming starts (to show loading indicator)
  useEffect(() => {
    if (isStreaming && messagesContainerRef.current) {
      userScrolledDuringStream.current = false; // Reset on new stream

      const scrollToBottom = () => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTo({
            top: messagesContainerRef.current.scrollHeight,
            behavior: "smooth",
          });
        }
      };

      // Scroll immediately and after short delay
      scrollToBottom();
      const timeoutId = setTimeout(scrollToBottom, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [isStreaming]);

  // Detect when user scrolls during streaming
  const handleScroll = () => {
    if (!messagesContainerRef.current) return;

    const container = messagesContainerRef.current;
    const isNearBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight <
      100;

    if (isStreaming) {
      if (!isNearBottom) {
        // User scrolled up during streaming - stop auto-scrolling, show button
        userScrolledDuringStream.current = true;
        setShowScrollToBottom(true);
      } else {
        // At bottom - resume auto-scrolling, hide button
        userScrolledDuringStream.current = false;
        setShowScrollToBottom(false);
      }
    } else {
      // Not streaming - just manage button visibility based on position
      setShowScrollToBottom(!isNearBottom && messages.length > 0);
    }
  };

  // Scroll to bottom handler (for button click)
  const handleScrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
      setShowScrollToBottom(false);
    }
  };

  // Handle paste events for images
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      const files: File[] = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === "file" && item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) {
            // Rename pasted images to "pasted-{timestamp}-{index}.ext" for uniqueness
            // Backend will add refId, final name will be: refId-pasted-{timestamp}-{index}.png
            const extension = file.type.split("/")[1] || "png";
            const timestamp = Date.now();
            const newName = `pasted-${timestamp}-${i}.${extension}`;
            const renamedFile = new File([file], newName, { type: file.type });
            files.push(renamedFile);
          }
        }
      }

      if (files.length > 0 && onFilesDropped) {
        e.preventDefault();
        onFilesDropped(files);
      }
    };

    window.addEventListener("paste", handlePaste);
    return () => {
      window.removeEventListener("paste", handlePaste);
    };
  }, [onFilesDropped]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Allow submission only when not disabled, not streaming, and container is ready
    if (prompt.trim() && !disabled && !isStreaming && isContainerReady) {
      // Build mention prefix from chips
      const mentionPrefix = mentionChips
        .map((c) => {
          if (c.type === "project") return `@project:${c.name}[${c.id}]`;
          if (c.type === "file") return `@file:${c.name}`;
          if (c.type === "skill") return `@skill:${c.id || c.name}`;
          return `@theme:${c.name}`;
        })
        .join(" ");
      const fullPrompt = mentionPrefix
        ? `${mentionPrefix} ${prompt.trim()}`
        : prompt.trim();
      onSendPrompt(fullPrompt);
      setPrompt("");
      setMentionChips([]);
      // Reset textarea height to original size (min-h-[44px] from CSS will take over)
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }
      // Reset scroll flag and scroll to bottom when user sends message
      userScrolledDuringStream.current = false;
      setTimeout(() => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTo({
            top: messagesContainerRef.current.scrollHeight,
            behavior: "smooth",
          });
        }
      }, 100);
      // Close mention dropdown
      setShowMentionDropdown(false);
    }
  };

  // Load current project files for @ mention Files tab (once on dropdown open)
  useEffect(() => {
    if (!showMentionDropdown || !projectId || mentionFiles.length > 0) return;
    editorAPI
      .listProjectFiles(projectId)
      .then((files) => {
        if (Array.isArray(files)) setMentionFiles(files);
      })
      .catch(() => {});
  }, [showMentionDropdown, projectId, mentionFiles.length]);

  // Fetch referenceable projects when mention dropdown opens
  useEffect(() => {
    if (!showMentionDropdown || !workspaceId) return;
    setIsLoadingProjects(true);
    editorAPI
      .searchReferenceableProjects(
        mentionQuery || undefined,
        projectId || undefined,
      )
      .then((data) => {
        if (Array.isArray(data)) setReferenceableProjects(data);
      })
      .catch(() => {})
      .finally(() => setIsLoadingProjects(false));
  }, [showMentionDropdown, mentionQuery, workspaceId, projectId]);

  // Theme editor hook for @ mentions (available in all sessions)
  const { themes, isLoading, currentTheme } = useThemeEditor({
    workspaceId,
    previewIframe,
    projectId,
  });

  // Handle mention selection — all types become chips (no text in textarea)
  const handleMentionSelect = useCallback(
    (selection: MentionSelection): void => {
      if (mentionStartIndex === null) return;

      // Remove the @ that triggered the dropdown
      const before = prompt.substring(0, mentionStartIndex);
      const after = prompt.substring(
        mentionStartIndex + 1 + mentionQuery.length,
      );
      setPrompt(`${before}${after}`);

      // Add as chip (deduplicate)
      setMentionChips((prev) => {
        const key = `${selection.type}:${selection.id || selection.name}`;
        if (prev.some((c) => `${c.type}:${c.id || c.name}` === key))
          return prev;
        return [
          ...prev,
          { type: selection.type, name: selection.name, id: selection.id },
        ];
      });

      setShowMentionDropdown(false);
      setMentionQuery("");
      setMentionStartIndex(null);
      setSelectedMentionIndex(0);

      textareaRef.current?.focus();
    },
    [mentionStartIndex, mentionQuery, prompt],
  );

  // Remove a mention chip
  const handleRemoveMentionChip = useCallback((index: number) => {
    setMentionChips((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Insert @ at cursor and open mention dropdown (triggered by "Add reference" button)
  const handleAddReference = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const cursorPos = textarea.selectionStart;
    const before = prompt.substring(0, cursorPos);
    const after = prompt.substring(cursorPos);
    const needsSpace =
      before.length > 0 && !before.endsWith(" ") && !before.endsWith("\n");
    const newText = before + (needsSpace ? " @" : "@") + after;
    const newCursorPos = cursorPos + (needsSpace ? 2 : 1);

    setPrompt(newText);
    setMentionQuery("");
    setMentionStartIndex(newCursorPos - 1);
    setShowMentionDropdown(true);
    setSelectedMentionIndex(0);

    // Focus and set cursor after the @
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    });
  }, [prompt]);

  // Handle prompt change with @ mention detection
  const handlePromptChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
      const value = e.target.value;
      const cursorPos = e.target.selectionStart;
      setPrompt(value);

      // Auto-resize textarea
      const textarea = e.target;
      textarea.style.height = "auto";
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;

      // Handle @ mentions for themes (available in all sessions)
      // Find @ symbol before cursor
      const textBeforeCursor = value.substring(0, cursorPos);
      const lastAtIndex = textBeforeCursor.lastIndexOf("@");

      if (lastAtIndex !== -1) {
        // Check if @ is at start or preceded by whitespace
        const charBeforeAt =
          lastAtIndex > 0 ? textBeforeCursor[lastAtIndex - 1] : " ";
        if (
          charBeforeAt === " " ||
          charBeforeAt === "\n" ||
          lastAtIndex === 0
        ) {
          const query = textBeforeCursor.substring(lastAtIndex + 1);
          // Only show dropdown if query doesn't contain whitespace (user is still typing the mention)
          if (!query.includes(" ") && !query.includes("\n")) {
            setMentionQuery(query);
            setMentionStartIndex(lastAtIndex);
            setShowMentionDropdown(true);
            setSelectedMentionIndex(0);
            return;
          }
        }
      }

      // Close dropdown if no valid @ pattern
      setShowMentionDropdown(false);
    },
    [],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Backspace on empty input removes last mention chip
    if (
      (e.key === "Backspace" || e.key === "Delete") &&
      prompt === "" &&
      mentionChips.length > 0 &&
      !showMentionDropdown
    ) {
      e.preventDefault();
      setMentionChips((prev) => prev.slice(0, -1));
      return;
    }

    // Handle mention dropdown navigation
    if (showMentionDropdown) {
      if (e.key === "Escape") {
        e.preventDefault();
        setShowMentionDropdown(false);
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        const max = mentionItemCountRef.current - 1;
        setSelectedMentionIndex((prev) => Math.min(prev + 1, Math.max(0, max)));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedMentionIndex((prev) => Math.max(0, prev - 1));
        return;
      }
      // Enter/Tab to select highlighted item
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        mentionConfirmRef.current?.();
        return;
      }
      // Left/Right arrows to switch tabs
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        mentionSwitchTabRef.current?.("left");
        return;
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        mentionSwitchTabRef.current?.("right");
        return;
      }
    }

    // Handle Enter key for submission (desktop only)
    // On mobile, Enter creates a new line; user taps send button to submit
    if (e.key === "Enter" && !e.shiftKey && !isMobile) {
      e.preventDefault();
      handleSubmit(e);
      return;
    }

    // Handle double ESC to clear chat input (similar to Claude Code)
    if (e.key === "Escape") {
      const now = Date.now();
      const timeSinceLastEsc = now - lastEscTimeRef.current;

      if (timeSinceLastEsc < ESC_TIMEOUT) {
        // Double ESC detected - clear the input
        e.preventDefault();
        setPrompt("");
        // Reset textarea height to original size
        if (textareaRef.current) {
          textareaRef.current.style.height = "auto";
        }
        lastEscTimeRef.current = 0; // Reset
      } else {
        // First ESC - record the time
        lastEscTimeRef.current = now;
      }
    } else {
      // Reset ESC timer if any other key is pressed
      lastEscTimeRef.current = 0;
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles && droppedFiles.length > 0 && onFilesDropped) {
      const filesArray = Array.from(droppedFiles);
      onFilesDropped(filesArray);
    }
  };

  // Get mode-specific helper text
  const getModeHelperText = () => {
    switch (mode) {
      case "plan":
        return "Analyze and plan";
      case "execute":
        return "Write and modify";
      default:
        return "Ask Claude";
    }
  };

  // Memoize message reordering to avoid O(n²) computation on every render
  const reorderedMessages = useMemo(() => {
    const result: Message[] = [];
    const assistantByUserMessage = new Map<string, Message[]>();
    const processedIds = new Set<string>();

    // First pass: group assistant messages by userMessageId (turn-based grouping)
    messages.forEach((msg) => {
      if (msg.type === MessageType.ASSISTANT && msg.userMessageId) {
        const existing = assistantByUserMessage.get(msg.userMessageId) || [];
        existing.push(msg);
        assistantByUserMessage.set(msg.userMessageId, existing);
      }
    });

    // Build set of user message IDs for quick lookup
    const userMessageIds = new Set(
      messages.filter((m) => m.type === MessageType.USER).map((m) => m.id),
    );

    // Second pass: build reordered list
    messages.forEach((msg) => {
      if (processedIds.has(msg.id)) return;

      if (msg.type === MessageType.USER) {
        result.push(msg);
        processedIds.add(msg.id);

        // Get all assistant messages that belong to this user message's turn
        const responses = assistantByUserMessage.get(msg.id) || [];
        responses.forEach((resp) => {
          // Skip if already processed (handles duplicates in source messages)
          if (processedIds.has(resp.id)) return;
          result.push(resp);
          processedIds.add(resp.id);
        });
      } else if (
        msg.type === MessageType.ASSISTANT &&
        msg.userMessageId &&
        userMessageIds.has(msg.userMessageId)
      ) {
        // Skip - will be added after parent user message (grouped by userMessageId)
      } else {
        // Add messages without userMessageId (legacy) or orphaned messages
        result.push(msg);
        processedIds.add(msg.id);
      }
    });

    // Final deduplication check - ensure unique IDs in result
    // Also assign fallback IDs for messages with missing IDs (data issue)
    const seenIds = new Set<string>();
    let fallbackCounter = 0;
    const deduped = result.map((msg, idx) => {
      let id = msg.id;

      // Assign fallback ID if missing
      if (!id) {
        id = `fallback-${msg.type}-${idx}-${++fallbackCounter}`;
        console.warn(
          "[AgentChat] Message missing ID, assigned fallback:",
          id,
          msg.type,
          msg.content?.slice(0, 50),
        );
      }

      // Handle duplicate IDs by appending index
      if (seenIds.has(id)) {
        const originalId = id;
        id = `${id}-dup-${idx}`;
        console.warn(
          "[AgentChat] Duplicate message ID detected:",
          originalId,
          "->",
          id,
          msg.type,
        );
      }

      seenIds.add(id);

      // Return message with guaranteed unique ID
      return msg.id === id ? msg : { ...msg, id };
    });

    return deduped;
  }, [messages]);

  return (
    <div
      className="flex flex-col h-full bg-background text-foreground relative"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Drag Overlay */}
      {isDragging && (
        <div className="absolute inset-0 z-50 bg-gradient-to-br from-blue-600/20 via-purple-600/20 to-blue-600/20 backdrop-blur-lg border-4 border-dashed border-blue-400/60 flex items-center justify-center rounded-lg">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-blue-500/30 border-3 border-blue-400/60 mb-6 shadow-2xl animate-bounce">
              <Upload className="w-12 h-12 text-blue-300" strokeWidth={2.5} />
            </div>
            <p className="text-3xl font-bold text-white mb-3 drop-shadow-2xl">
              Drop files here
            </p>
            <p className="text-base text-gray-200 drop-shadow-lg">
              Images, videos, PDFs up to 50MB
            </p>
          </div>
        </div>
      )}

      {/* Messages - cleaner background - only show when chat tab is active */}
      {currentActiveTab === "chat" && (
        <div className="relative flex-1 min-h-0">
          <div
            ref={messagesContainerRef}
            onScroll={handleScroll}
            className="h-full overflow-y-auto overflow-x-hidden bg-background scrollbar-hide"
          >
            {messages.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center p-8 max-w-md">
                  <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                    <Bot className="w-6 h-6 text-muted-foreground" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">
                    No messages yet
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Start a conversation to get assistance with code, debugging,
                    or planning.
                  </p>
                  {/* Credits display for new session */}
                  {numBalance !== null && !isLoadingCredits && (
                    <>
                      <div className="w-12 h-px bg-border mx-auto mt-4" />
                      <p className="text-sm text-foreground/80 mt-2">
                        Credits remaining: {numBalance.toFixed(2)}
                      </p>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="p-3">
                {reorderedMessages.map((message, index) => {
                  const prevMessage =
                    index > 0 ? reorderedMessages[index - 1] : null;
                  const isConsecutiveAssistant =
                    message.type === MessageType.ASSISTANT &&
                    prevMessage?.type === MessageType.ASSISTANT &&
                    message.userMessageId === prevMessage.userMessageId;

                  return (
                    <div
                      key={message.id}
                      className={
                        isConsecutiveAssistant ? "" : index > 0 ? "mt-2" : ""
                      }
                    >
                      <AgentMessage
                        message={message}
                        onOpenFile={onOpenFile}
                        onAction={onAction}
                        showHeader={
                          message.type !== MessageType.ASSISTANT ||
                          index === 0 ||
                          prevMessage?.type !== MessageType.ASSISTANT ||
                          // Show header if responding to different user message (turn-based grouping)
                          !!(
                            message.userMessageId &&
                            prevMessage?.userMessageId &&
                            message.userMessageId !== prevMessage.userMessageId
                          )
                        }
                        streamingMessage={streamingMessage}
                        workspaceId={workspaceId}
                        previewIframe={previewIframe}
                        projectId={projectId || ""}
                        onEditTheme={onEditTheme}
                        onThemeSaved={onThemeSaved}
                        onConnectionCreated={onConnectionCreated}
                        devContainerId={devContainerId}
                        previewUrl={previewUrl}
                      />
                    </div>
                  );
                })}
                {isStreaming && messages.length === 0 && (
                  <div className="p-2.5">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-muted text-muted-foreground">
                        <Bot className="w-4 h-4" />
                      </div>
                      <span className="text-sm font-medium text-foreground">
                        Agent
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                      <span>{streamingMessage}</span>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Scroll to bottom button */}
          {showScrollToBottom && (
            <button
              type="button"
              onClick={handleScrollToBottom}
              className="absolute bottom-3 right-3 z-10 p-2 rounded-full bg-muted hover:bg-accent border border-border shadow-lg transition-all duration-200"
              aria-label="Scroll to bottom"
            >
              <ArrowDown className="w-4 h-4 text-foreground" />
            </button>
          )}
        </div>
      )}

      {/* Input - cleaner styling */}
      <div className="bg-background p-3 space-y-2">
        {/* Container Setup Indicator - shows during instant resume */}
        {!isContainerReady && (
          <div className="flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 dark:border-primary/30 dark:bg-primary/10 px-3 py-1.5 text-xs text-foreground">
            <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />
            <span>Setting things up...</span>
          </div>
        )}

        {/* Credits Warning - shows at 80% used, 90% used, or negative balance */}
        {/* Hidden when BYOC connected (user pays Anthropic directly) */}
        {creditBalance !== null && (
          <CreditsWarning
            balance={creditBalance}
            allocatedCredits={allocatedCredits ?? undefined}
            onGetCredits={() => navigate("/dashboard/settings/billing")}
            isTyping={prompt.length > 0}
            shownAlerts={shownCreditAlerts}
            byocConnected={byocConnected}
            onAlertShown={onCreditAlertShown}
          />
        )}

        {/* Email Verification Warning - shows when user needs to verify email */}
        {isEmailVerificationBlocked && (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-primary/30 bg-primary/5 dark:border-primary/30 dark:bg-primary/10 px-3 py-1.5 text-xs text-foreground">
            <span>
              <span className="text-primary font-medium">
                Email not verified:
              </span>{" "}
              Please verify your email to continue. Check your inbox for the
              verification link.
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onResendVerificationEmail}
                disabled={
                  isResendingVerificationEmail || isCheckingEmailVerification
                }
                className="font-medium rounded-md border border-primary/50 text-primary hover:bg-primary/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors px-2.5 py-1 text-xs whitespace-nowrap flex items-center gap-1.5"
              >
                {isResendingVerificationEmail ? (
                  <>
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Sending...
                  </>
                ) : (
                  "Resend Email"
                )}
              </button>
              <button
                type="button"
                onClick={onCheckEmailVerification}
                disabled={
                  isCheckingEmailVerification || isResendingVerificationEmail
                }
                className="font-medium rounded-md bg-primary text-white hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors px-2.5 py-1 text-xs whitespace-nowrap flex items-center gap-1.5"
              >
                {isCheckingEmailVerification ? (
                  <>
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Checking...
                  </>
                ) : (
                  "I've Verified"
                )}
              </button>
            </div>
          </div>
        )}

        {/* Connection Lost Banner - shows when WebSocket disconnects unexpectedly */}
        {connectionLost && (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-primary/30 bg-primary/5 dark:border-primary/30 dark:bg-primary/10 px-3 py-1.5 text-xs text-foreground">
            <span>
              <span className="text-primary font-medium">Connection lost.</span>{" "}
              Please refresh and try again.
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onConnectionLostDismiss}
                className="font-medium rounded-md border border-primary/50 text-primary hover:bg-primary/10 transition-colors px-2.5 py-1 text-xs whitespace-nowrap"
              >
                Dismiss
              </button>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="font-medium rounded-md bg-primary text-white hover:bg-primary/90 transition-colors px-2.5 py-1 text-xs whitespace-nowrap flex items-center gap-1.5"
              >
                <RefreshCw className="w-3 h-3" />
                Refresh
              </button>
            </div>
          </div>
        )}

        {/* Viewer Mode Warning - shows when user is a viewer */}
        {isViewer && (
          <div className="flex items-center gap-3 rounded-lg border border-muted bg-muted/50 px-3 py-1.5 text-xs text-muted-foreground">
            <Eye className="w-4 h-4 flex-shrink-0" />
            <span>
              <span className="font-medium">View only:</span> You have read-only
              access to this workspace. Contact an admin to request edit
              permissions.
            </span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div
            className={cn(
              "relative flex flex-col p-3 gap-2 bg-card focus-within:border-border/50",
              // Desktop: full rounded border
              !isMobile && "border border-border/50 rounded-2xl",
              // Mobile: rounded border, with safe area padding at bottom
              isMobile &&
                "border border-border/50 rounded-2xl pb-[env(safe-area-inset-bottom)]",
              isChatBlocked && "opacity-50 pointer-events-none blur-[2px]",
            )}
          >
            {/* Attached Files Preview - Compact thumbnails */}
            {files.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {files.map((fileWithState) => {
                  const preview = filePreviews.get(fileWithState.file.name);
                  const isUploading =
                    fileWithState.uploadState === "uploading" ||
                    fileWithState.uploadState === "pending";
                  const isFailed = fileWithState.uploadState === "failed";

                  return (
                    <div
                      key={fileWithState.file.name}
                      className="relative group bg-muted rounded-xl overflow-visible transition-colors"
                      style={{ width: "56px", height: "56px" }}
                    >
                      {/* Image preview or file icon */}
                      {preview ? (
                        <img
                          src={preview}
                          alt={fileWithState.file.name}
                          className={cn(
                            "w-full h-full object-cover",
                            isUploading && "opacity-50",
                          )}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-muted rounded-xl">
                          {fileWithState.file.type?.includes("pdf") ? (
                            <FileText
                              className={cn(
                                "w-6 h-6 text-red-400",
                                isUploading && "opacity-50",
                              )}
                            />
                          ) : fileWithState.file.type?.includes("video") ? (
                            <FileIcon
                              className={cn(
                                "w-6 h-6 text-purple-400",
                                isUploading && "opacity-50",
                              )}
                            />
                          ) : (
                            <FileIcon
                              className={cn(
                                "w-6 h-6 text-blue-400",
                                isUploading && "opacity-50",
                              )}
                            />
                          )}
                        </div>
                      )}

                      {/* Upload progress overlay */}
                      {isUploading && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-xl">
                          <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
                        </div>
                      )}

                      {/* Hover overlay with filename */}
                      <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center p-0.5 rounded-xl">
                        <span className="text-[8px] text-white text-center truncate w-full px-0.5 leading-tight">
                          {fileWithState.file.name}
                        </span>
                        {isFailed && (
                          <span className="text-[8px] text-red-400">
                            Failed
                          </span>
                        )}
                      </div>

                      {/* Remove button (only when not uploading) */}
                      {onRemoveAttachment && !isUploading && (
                        <button
                          type="button"
                          onClick={() =>
                            onRemoveAttachment(fileWithState.file.name)
                          }
                          className="absolute -top-1.5 -right-1.5 bg-red-500 hover:bg-red-600 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                        >
                          <X className="w-3 h-3 text-white" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* @ Mention dropdown (All/Files/Projects tabs - Lovable-style) */}
            <MentionDropdown
              isOpen={showMentionDropdown}
              query={mentionQuery}
              selectedIndex={selectedMentionIndex}
              onIndexChange={setSelectedMentionIndex}
              onSelect={handleMentionSelect}
              confirmSelectionRef={mentionConfirmRef}
              switchTabRef={mentionSwitchTabRef}
              itemCountRef={mentionItemCountRef}
              files={mentionFiles}
              projects={referenceableProjects}
              themes={themes}
              currentTheme={currentTheme}
              isLoadingProjects={isLoadingProjects}
            />

            {/* Skill picker is now embedded in AttachmentDropdown as a submenu */}

            {/* Mention chips (projects, files, themes, skills) */}
            {mentionChips.length > 0 && (
              <div className="flex flex-wrap gap-1.5 px-2 pb-1">
                {mentionChips.map((chip, idx) => (
                  <MentionBadge
                    key={`${chip.type}-${chip.id || chip.name}`}
                    type={chip.type}
                    name={chip.name}
                    id={chip.id}
                    onRemove={() => handleRemoveMentionChip(idx)}
                  />
                ))}
              </div>
            )}

            {/* Textbox with inline voice button on mobile */}
            <div className="flex items-center gap-2">
              <Textarea
                ref={textareaRef}
                value={prompt}
                onChange={handlePromptChange}
                onKeyDown={handleKeyDown}
                placeholder={
                  isStreaming
                    ? "Stop to send another message..."
                    : files.length > 0
                      ? `What would you like to do with ${files.length > 1 ? "these files" : "this file"}?`
                      : "Ask OptiDev..."
                }
                disabled={disabled}
                className={cn(
                  "flex-1 min-h-[44px] max-h-[200px] resize-none overflow-y-auto",
                  "bg-transparent border-0 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-sm text-foreground",
                  "placeholder:text-muted-foreground/60 py-2 px-2",
                )}
                rows={1}
              />

              {/* Voice/Send/Stop button - inline with textarea on mobile */}
              {isMobile &&
                (isStreaming ? (
                  // Stop button when streaming - matches desktop pattern
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => {
                      if (onStopChat) {
                        onStopChat();
                      }
                    }}
                    className="h-8 w-8 rounded-full flex-shrink-0 hover:bg-destructive/10 stop-button-ring"
                    title="Stop generation"
                  >
                    <StopCircle className="w-4 h-4 text-destructive" />
                  </Button>
                ) : prompt.trim() || files.length > 0 ? (
                  // Send button when there's content
                  <Button
                    type="submit"
                    size="icon"
                    variant="ghost"
                    disabled={disabled || isChatBlocked || !isContainerReady}
                    className="h-8 w-8 rounded-full flex-shrink-0 bg-purple-600 hover:bg-purple-700 text-white"
                    title={!isContainerReady ? "Setting up..." : "Send message"}
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                ) : (
                  // Mic button when idle and no content
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    disabled={disabled || isProcessingVoice || isChatBlocked}
                    onClick={handleVoiceToggle}
                    className={cn(
                      "h-8 w-8 rounded-md flex-shrink-0",
                      isRecording && "text-destructive animate-pulse",
                    )}
                    title={isRecording ? "Stop recording" : "Start voice input"}
                  >
                    {isProcessingVoice ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Mic
                        className={cn("w-4 h-4", isRecording && "fill-current")}
                      />
                    )}
                  </Button>
                ))}
            </div>

            {/* Controls row below textbox */}
            {isMobile ? (
              /* Mobile: + | Chat | Preview | ... */
              <div className="flex items-center gap-2 pt-1 pb-2">
                {/* Left: Attachment dropdown (image gen, attach files, etc.) */}
                <div className="flex-shrink-0">
                  <AttachmentDropdown
                    onSelectAttach={onSelectAttach}
                    onSelectConnections={onSelectConnections}
                    onSelectProjectSettings={onSelectProjectSettings}
                    onSelectImageGenerator={onSelectImageGenerator}
                    onSelectMediaLibrary={onSelectMediaLibrary}
                    onSelectThemes={onSelectThemes}
                    onSelectAddReference={handleAddReference}
                    onSelectSkill={(skill) => {
                      setMentionChips((prev) => {
                        const exists = prev.some(
                          (c) => c.type === "skill" && c.id === skill.id,
                        );
                        if (exists) return prev;
                        return [
                          ...prev,
                          { type: "skill", name: skill.name, id: skill.id },
                        ];
                      });
                    }}
                    selectedSkillIds={mentionChips
                      .filter((c) => c.type === "skill" && c.id)
                      .map((c) => c.id!)}
                    disabled={isStreaming || disabled}
                    isMobile={isMobile}
                  />
                </div>

                {/* Center: Chat | Preview tabs */}
                <div className="flex-1 flex items-center justify-center">
                  <div className="flex items-center bg-muted rounded-xl p-1">
                    <button
                      type="button"
                      onClick={() => onMobileActiveTabChange?.("chat")}
                      className={cn(
                        "flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-colors",
                        currentActiveTab === "chat"
                          ? "bg-background text-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground",
                      )}
                    >
                      <MessageCircle className="w-4 h-4" />
                      Chat
                    </button>
                    <button
                      type="button"
                      onClick={() => onMobileActiveTabChange?.("preview")}
                      className={cn(
                        "flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-colors",
                        currentActiveTab === "preview"
                          ? "bg-background text-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground",
                      )}
                    >
                      <Eye className="w-4 h-4" />
                      Preview
                    </button>
                  </div>
                </div>

                {/* Right: More options button */}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={onOpenSettings}
                  className="h-9 w-9 rounded-xl bg-muted flex-shrink-0"
                >
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              /* Desktop: Original layout */
              <div className="flex items-center gap-1.5 justify-between">
                {/* Left side controls */}
                <div className="flex items-center gap-1.5">
                  {/* Attachment dropdown button */}
                  <AttachmentDropdown
                    onSelectAttach={onSelectAttach}
                    onSelectConnections={onSelectConnections}
                    onSelectProjectSettings={onSelectProjectSettings}
                    onSelectImageGenerator={onSelectImageGenerator}
                    onSelectMediaLibrary={onSelectMediaLibrary}
                    onSelectThemes={onSelectThemes}
                    onSelectAddReference={handleAddReference}
                    onSelectSkill={(skill) => {
                      setMentionChips((prev) => {
                        const exists = prev.some(
                          (c) => c.type === "skill" && c.id === skill.id,
                        );
                        if (exists) return prev;
                        return [
                          ...prev,
                          { type: "skill", name: skill.name, id: skill.id },
                        ];
                      });
                    }}
                    selectedSkillIds={mentionChips
                      .filter((c) => c.type === "skill" && c.id)
                      .map((c) => c.id!)}
                    disabled={isStreaming || disabled}
                    isMobile={isMobile}
                  />

                  <AgentModeDropdown
                    mode={mode}
                    onChange={onModeChange}
                    disabled={isStreaming}
                  />

                  <ModelDropdown
                    model={model}
                    onChange={onModelChange}
                    disabled={isStreaming}
                  />
                </div>

                {/* Right side controls */}
                <div className="flex items-center gap-1.5">
                  {/* Voice input button */}
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    disabled={
                      disabled ||
                      isStreaming ||
                      isProcessingVoice ||
                      isChatBlocked
                    }
                    onClick={handleVoiceToggle}
                    className={cn(
                      "h-8 w-8 rounded-md",
                      isRecording && "text-destructive animate-pulse",
                    )}
                    title={isRecording ? "Stop recording" : "Start voice input"}
                  >
                    {isProcessingVoice ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Mic
                        className={cn("w-4 h-4", isRecording && "fill-current")}
                      />
                    )}
                  </Button>

                  {/* Send/Stop button - single button to prevent click bleed-through on state change */}
                  <Button
                    type="button"
                    disabled={
                      !isStreaming &&
                      (!prompt.trim() ||
                        disabled ||
                        !isContainerReady ||
                        files.some(
                          (f) =>
                            f.uploadState === "uploading" ||
                            f.uploadState === "pending",
                        ))
                    }
                    onClick={() => {
                      if (isStreaming && onStopChat) {
                        onStopChat();
                      } else if (
                        prompt.trim() &&
                        !disabled &&
                        !isStreaming &&
                        isContainerReady
                      ) {
                        const mentionPrefix = mentionChips
                          .map((c) => {
                            if (c.type === "project")
                              return `@project:${c.name}[${c.id}]`;
                            if (c.type === "file") return `@file:${c.name}`;
                            return `@theme:${c.name}`;
                          })
                          .join(" ");
                        const fullPrompt = mentionPrefix
                          ? `${mentionPrefix} ${prompt.trim()}`
                          : prompt.trim();
                        onSendPrompt(fullPrompt);
                        setPrompt("");
                        setMentionChips([]);
                        // Reset textarea height to original size (min-h-[44px] from CSS will take over)
                        if (textareaRef.current) {
                          textareaRef.current.style.height = "auto";
                        }
                        userScrolledDuringStream.current = false;
                        setTimeout(() => {
                          if (messagesContainerRef.current) {
                            messagesContainerRef.current.scrollTo({
                              top: messagesContainerRef.current.scrollHeight,
                              behavior: "smooth",
                            });
                          }
                        }, 100);
                      }
                    }}
                    size="icon"
                    variant={isStreaming ? "ghost" : "default"}
                    className={
                      isStreaming
                        ? "h-8 w-8 rounded-full hover:bg-destructive/10 stop-button-ring"
                        : "h-8 w-8 rounded-md bg-muted hover:bg-accent text-foreground disabled:opacity-50"
                    }
                  >
                    {isStreaming ? (
                      <StopCircle className="w-4 h-4 text-destructive" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </form>
      </div>

      {/* Voice Recording Dialog */}
      <Dialog
        open={isRecording}
        onOpenChange={(open) => {
          if (!open) stopRecording();
        }}
      >
        <DialogContent
          className="max-w-[90vw] sm:max-w-md mx-auto rounded-3xl"
          hideCloseButton
          onEscapeKeyDown={(e) => {
            e.preventDefault();
            stopRecording();
          }}
        >
          <div className="flex flex-col items-center justify-center py-6 sm:py-8 px-4 sm:px-6">
            {/* Animated Microphone Icon */}
            <div className="relative mb-4 sm:mb-6">
              <div className="absolute inset-0 bg-purple-500/20 rounded-full animate-ping" />
              <div className="relative bg-purple-600 text-white p-5 sm:p-6 rounded-full">
                <Mic className="w-10 h-10 sm:w-12 sm:h-12" />
              </div>
            </div>

            {/* Listening Text */}
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-2">
              Listening...
            </h2>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 text-center max-w-xs px-2">
              Speak clearly into your microphone
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
